import { SearchConfigService } from './../search/search-config.service';

import { Injectable } from "@angular/core";
import {
  DeltaService,
  EntityNames,
  EntitySyncInfo
} from "../../data-services/delta/delta.service";
import { NavigationService } from "../navigation/navigation.service";
import { AuthenticationService } from "../authentication.service";
import { EventsService } from "../events/events.service";
import { DiskService, OFFLINE_DATA_COUNT_ENTITY_NAME } from "../disk/disk.service";
import { FeatureActionsMap, IoConfigurationFeatureCodes } from "../../classes/authentication/user.class";
import {
  DB_ALLDOCS_QUERY_OPTIONS,
  DB_KEY_PREFIXES,
  DB_SYNC_STATE_KEYS
} from "../../config/pouch-db.config";
import { Endpoints } from "../../../config/endpoints.config";
import { ConsentDataService } from "../../data-services/consent/consent.data.service";
import { ContactConsent } from "../../classes/consent/contact-consent.class";
import { BehaviorSubject } from "rxjs";
import { ConsentTerm } from "../../classes/consent/consent-term.class";
import {Channel, ChannelType, ChannelValue, ChannelActivityType, BusinessUnitChannel} from "../../classes/consent/channel.class";
import { Brand } from "../../classes/brand/brand.class";
import { DeviceService } from "../device/device.service";
import {TrackAction} from "../../utility/common-enums";
import {NotificationService, ToastStyle} from "../notification/notification.service";
import {Contact, ContactAddress, ContactDTO, Email} from "../../classes/contact/contact.class";
import {ContactDataService} from "../../data-services/contact/contact.data.service";
import {LoadingController} from "@ionic/angular";
import {ContactOfflineService} from "../contact/contact.service";
import { TranslateService } from "@ngx-translate/core";
import { DateTimeFormatsService } from '../date-time-formats/date-time-formats.service';
import {ConsentedContact} from "../../classes/activity/email.activity.class";
import { fetchQueries } from '@omni/config/dynamics-fetchQueries';
import { DynamicsClientService } from '@omni/data-services/dynamics-client/dynamics-client.service';
import { ConsentActivity } from '@omni/classes/consent/consent-activity.class';
import { Product } from '@omni/classes/consent/product.class';
import { Topic } from '@omni/classes/consent/topic.class';
import { SyncProfilerService } from '../logging/sync-profiler.service';
import { cloneDeep, groupBy, isArray, isEmpty, pick, uniqBy } from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class ConsentService {
  public allActiveConsentSubject = new BehaviorSubject<ContactConsent[]>([]);
  public allActiveConsentSubject$ = this.allActiveConsentSubject.asObservable();
  private activeConsentsLoadedFromDiskPromise: Promise<void> | null = null;
  public allConsentChannelSubject = new BehaviorSubject<Channel[]>([]);
  public allConsentTermsSubject = new BehaviorSubject<ConsentTerm[]>([]);
  public allConsentTermsAllocationOrdersSubject = new BehaviorSubject<ConsentTerm[]>([]);
  public showAllConsentsFilter$ = new BehaviorSubject<boolean>(false);
  public showAllConsentsFilter: string = 'All';
  public showAllConsentFilterObserver$ = this.showAllConsentsFilter$.asObservable();
  public selectedConsentTerm = new BehaviorSubject<ConsentTerm>(null);
  public selectedCTObserver$ = this.selectedConsentTerm.asObservable();
  public currentConsentTerm = new BehaviorSubject<ConsentTerm>(null);
  public currentCTObserver$ = this.currentConsentTerm.asObservable();
  public consentProductFilter$: Brand;
  public consentChannelFilter$: Channel;
  public isProductFilterExpanded: boolean;
  public isChannelFilterExpanded: boolean;
  public consentTab: string = "";
  public allGeneratedTerms = new BehaviorSubject<ConsentTerm[]>([]);
  public allGeneratedTermsObs$ = this.allGeneratedTerms.asObservable();
  public savedConsents: ConsentTerm[];
  public savedChannels: Channel[];
  inMeetingConsentFlow: boolean = false;
  consentRemoteFlow: boolean = false;
  allocationRemoteFlow: boolean = false;
  public businessunitChannels = []
  public allConsentActivitiesWithProductsAndEmails = []
  public allDeletedActiveConsents = []


  constructor(
    private navService: NavigationService,
    private disk: DiskService,
    private authService: AuthenticationService,
    private events: EventsService,
    private deltaService: DeltaService,
    private consentDataService: ConsentDataService,
    private deviceService: DeviceService,
    public notificationService: NotificationService,
    private contactDataService: ContactDataService,
    public contactService: ContactOfflineService,
    private loadingCtrl: LoadingController,
    private translate: TranslateService,
    public searchConfig: SearchConfigService,
    private dynamics: DynamicsClientService,
    private readonly syncProfilerService: SyncProfilerService,
    private readonly dateTimeFormatsService: DateTimeFormatsService,
  ) {
    this.events.observe("sync:completed").subscribe(() => {
      this.loadAndMapConsentsMasterDataFromDB();
    });
  }

  /**
   * Ensures `allActiveConsentSubject` is hydrated from disk at least once.
   * This prevents features like Spotlight from seeing an empty array on app relaunch before the next sync completes.
   */
  public ensureActiveConsentsLoadedFromDisk(): Promise<void> {
    if (this.activeConsentsLoadedFromDiskPromise) return this.activeConsentsLoadedFromDiskPromise;

    this.activeConsentsLoadedFromDiskPromise = this.disk
      .batchFetch(DB_ALLDOCS_QUERY_OPTIONS.GET_ALL_ACTIVE_CONSENTS)
      .then((data: ContactConsent[]) => {
        if (data) {
          data.forEach(cct => {
            if (Array.isArray(cct.activeConsents)) {
              cct.activeConsents.forEach(term => {
                term.isOfflineSaved = false;
                term.isSelectedTerm = false;
              });
            }
          });
        }

        this.allActiveConsentSubject.next(data || []);
      });

    return this.activeConsentsLoadedFromDiskPromise;
  }

  /**
   * Resets the active-consents disk-load cache so the next call to
   * `ensureActiveConsentsLoadedFromDisk` performs a fresh read.
   * Must be called on logout / session switch to prevent the previous
   * user's consent data from leaking into the next session.
   * TODO: wire this up in the logout / session-clear flow.
   */
  public resetActiveConsentsCache(): void {
    this.activeConsentsLoadedFromDiskPromise = null;
    this.allActiveConsentSubject.next([]);
  }

  public async loadAndMapConsentsMasterDataFromDB() {
    await this.disk
      .batchFetch(DB_ALLDOCS_QUERY_OPTIONS.GET_ALL_CONSENT_CHANNELS)
      .then((data: Channel[]) => {
        this.allConsentChannelSubject.next(data);
        this.savedChannels = data;
        console.log(
          `active consent channels from disk : ${data ? data.length : 0}`
        );
      });

    if (this.authService.hasFeatureAction(FeatureActionsMap.CONSENT_TOOL)) {
      // Keep active consents hydrated when loading master data from DB.
      await this.ensureActiveConsentsLoadedFromDisk();
    }

    if (this.authService.hasFeatureAction(FeatureActionsMap.CONSENT_TOOL)) {
      await this.disk
        .retrieve(DB_KEY_PREFIXES.BUSINESS_UNIT_CHANNELS)
        .then((doc) => {
          if (doc && doc.raw && doc.raw.length && this.businessunitChannels.length == 0) {
            this.businessunitChannels = doc.raw;
            console.log(`all business unit consents from disk : ${this.businessunitChannels ? this.businessunitChannels.length : 0}`);
          }
        });
        // Load active consents from DB
        await this.reloadActiveConsentsFromDB();
         // Load consent activities from DB
        await this.disk.retrieve(DB_KEY_PREFIXES.CONSENT_ACTIVITIES, true).then((doc) => {
          if (doc && doc.raw) {
            this.allConsentActivitiesWithProductsAndEmails = doc.raw;
            console.log(`consent activities from disk : ${this.allConsentActivitiesWithProductsAndEmails ? this.allConsentActivitiesWithProductsAndEmails.length : 0}`);
          } else {
            this.allConsentActivitiesWithProductsAndEmails = [];
            console.log(`consent activities from disk : 0 (no data found)`);
          }
        });
    }
    
    if (this.authService.hasFeatureAction(FeatureActionsMap.ALLOCATION_TOOL) || this.authService.hasFeatureAction(FeatureActionsMap.ALLOCATION_ORDER_ACTIVITY)) {
      await this.disk
        .batchFetch(DB_ALLDOCS_QUERY_OPTIONS.GET_ALL_CONSENT_TERMS_ALLOCATION_ORDERS)
        .then((data: ConsentTerm[]) => {
          this.allConsentTermsAllocationOrdersSubject.next(data);
          console.log(
            `active consent terms for allocation orders from disk : ${data ? data.length : 0}`
          );
        });
    }
  }

  public async getAllActiveConsentsMasterData(forceFullSync, loadFromDbOnly = false, profilerParentSpanId?: string) {
    if (loadFromDbOnly) {
      await this.loadAndMapConsentsMasterDataFromDB();
    } else {
      // Group A: channels + BU channels in parallel (no dependencies between them)
      await Promise.all([
        this.syncProfilerService.timeAsync('consents_channels', () => this.getAllActiveChannels(), profilerParentSpanId),
        this.syncProfilerService.timeAsync('consents_bu_channels', () => this.getAllBusinessUnitChannelMappings(loadFromDbOnly), profilerParentSpanId),
      ]);

      // Group B: remaining operations in parallel (all depend on channels from Group A being complete)
      // - consents_terms depends on savedChannels (written by getAllActiveChannels above)
      // - consents_active, consents_activities, consents_terms_allocation are fully independent
      const groupB: Promise<any>[] = [];
      if (this.authService.hasFeatureAction(FeatureActionsMap.CONSENT_TOOL)) {
        groupB.push(this.syncProfilerService.timeAsyncWithSpan('consents_active', (spanId) => this.getAllActiveConsents(false, spanId), profilerParentSpanId));
        groupB.push(this.syncProfilerService.timeAsync('consents_terms', () => this.getAllConsentTerms(), profilerParentSpanId));
        groupB.push(this.syncProfilerService.timeAsync('consents_activities', () => this.getAllConsentActivitiesForProductsAndEmails(forceFullSync), profilerParentSpanId));
      }
      if (this.authService.hasFeatureAction(FeatureActionsMap.ALLOCATION_TOOL)
        || this.authService.hasFeatureAction(FeatureActionsMap.ALLOCATION_ORDER_ACTIVITY)
      ) {
        groupB.push(this.syncProfilerService.timeAsync('consents_terms_allocation', () => this.getAllConsentTermsForAllocationOrders(), profilerParentSpanId));
      }
      await Promise.all(groupB);
      this.searchConfig.isConsentsMappedToContacts = false;
    }
  }

  private async getAllDeletedActiveConsents(lastModifiedDate, forceFullSync) {
    if (forceFullSync) {
      await this.disk.deleteAllFromDbUsingAlldocsQuery(DB_ALLDOCS_QUERY_OPTIONS.GET_ALL_DELETED_ACTIVE_CONSENTS);
      return;
    }

    await this.getAllDeletedActiveConsentsFromDB();


    const deletedActiveConsents = await this.consentDataService.getDeletedActiveConsents(lastModifiedDate)

    if (deletedActiveConsents && isArray(deletedActiveConsents) && !isEmpty(deletedActiveConsents)) {
      if (this.allDeletedActiveConsents && !isEmpty(this.allDeletedActiveConsents)) {
        this.allDeletedActiveConsents = uniqBy(this.allDeletedActiveConsents.concat(deletedActiveConsents),'indskr_activeConsent')
      } else this.allDeletedActiveConsents = deletedActiveConsents
    }

    await this.disk.updateOrInsert(DB_KEY_PREFIXES.DELETED_ACTIVE_CONSENTS, (doc) => {
      doc = {
        raw: this.allDeletedActiveConsents,
        lastDeltaSync: new Date().getTime()
      };
      return doc;
    })

    return deletedActiveConsents;
  }

   async getAllDeletedActiveConsentsFromDB() {
    await this.disk.retrieve(DB_KEY_PREFIXES.DELETED_ACTIVE_CONSENTS, true).then((doc) => {
      if (doc && doc.raw) {
        this.allDeletedActiveConsents = doc.raw;
      }
      else {
        this.allDeletedActiveConsents = [];
      }
    });
  }

  private async getAllActiveConsents(forceFullSync = false, profilerParentSpanId?: string) {
    if(forceFullSync) {
      await this.disk.deleteAllFromDbUsingAlldocsQuery(DB_ALLDOCS_QUERY_OPTIONS.GET_ALL_ACTIVE_CONSENTS);
    }
    const syncState = await this.disk.getSyncState(
      DB_SYNC_STATE_KEYS.SYNC_CONSENTS
    );
    const isInitialSync = !syncState || !syncState.lastUpdatedTime || forceFullSync;
    const newLastUpdatedTime = new Date().getTime();
    const consentSyncInfo: EntitySyncInfo = {
      entityName: EntityNames.consent,
      totalFailed: 0,
      totalSynced: 0,
      errors: [],
      syncStatus: true
    };
    await this.syncProfilerService.timeAsync('consents_active_api',
      () => this.consentDataService.getActiveConsents(isInitialSync, syncState.lastUpdatedTime),
      profilerParentSpanId)
      .then(async (data: ContactConsent[]) => {
        await this.syncProfilerService.timeAsync('consents_active_processing', async () => {
          console.log("Saving active consents to disk...");
           // get the Deleted Active Consents
           let deletedActiveConsents;

           if (!isInitialSync){
            deletedActiveConsents = await this.getAllDeletedActiveConsents(syncState.lastUpdatedTime, forceFullSync)
          }

          try {
            await this.saveConsentsToDisk(
              isInitialSync,
              DB_KEY_PREFIXES.CONSENTS,
              data,
              deletedActiveConsents
            );
          } catch (error) {
            // For now, only consider the service failure as sync failure..
            console.error('getAllActiveConsents: ', error);
          }
          await this.loadAndRefreshActiveConsents();
          await this.finalizeSyncState(syncState, consentSyncInfo, data, isInitialSync, newLastUpdatedTime);
        }, profilerParentSpanId);
      })
      .catch(err => {
        this.handleSyncError(err, consentSyncInfo, Endpoints.consent.GET_ACTIVE_CONSENTS, "Error occurred while fetching active consents...");
      });
  }

  private async getAllActiveConsentsByContactId(contactId:string) {
    await this.consentDataService.getActiveConsentsByContactId(contactId)
      .then((data: ContactConsent[]) => {
        console.log("Saving active consents to disk...");
        try {
          if(!isEmpty(data)) {
            // set initial sync true since we get all consents for the customer again
            this.saveConsentsToDisk(
              true,
              DB_KEY_PREFIXES.CONSENTS,
              data
            );
          }
        } catch (error) {
          console.error('getAllActiveConsentsById: ', error);
        }
      })
  }


   public async getAllBusinessUnitChannelMappings(fullSync = false, loadFromDBOnly = false) {

    let lastModifiedForDeltaSync, hourDifference;

    await this.disk.retrieve(DB_KEY_PREFIXES.BUSINESS_UNIT_CHANNELS).then((doc)=>{
      if(doc && doc.raw && doc.raw.length){
        this.businessunitChannels = doc.raw;
        lastModifiedForDeltaSync = doc.lastUpdatedTime;
      }
      else{
        this.businessunitChannels = [];
        fullSync = true;
      }
    });

    if (loadFromDBOnly) return;
    let deltaSyncFilter = "";
    let fetchXML = fetchQueries.businessUnitChannels;
    let now = new Date();

    if (lastModifiedForDeltaSync) {
      hourDifference = this.dateTimeFormatsService.getDifferenceInHours(
        now,
        new Date(lastModifiedForDeltaSync)
      )
      //add one to make sure we take care of fractional difference in hours
      hourDifference += 1
      deltaSyncFilter = `<filter type="and">
                            <condition attribute="modifiedon" operator="last-x-hours" value="${hourDifference}" />
                          </filter>`
    }

    fetchXML = fetchXML.replace('{deltaSyncFilter}', deltaSyncFilter);

    let arrDeletedMappings = []

    await this.dynamics.executeFetchQuery('indskr_businessunitchannels', fetchXML).then(async (data)=>{
      if(!fullSync) {
        let deletedMappings = await this.fetchDeletedBusinessunitChannelmappings(lastModifiedForDeltaSync);
        deletedMappings.sort((a,b)=>{
          if(a['track_action_CreatedOn']>b['track_action_CreatedOn']) return 1;
          else return -1;
        });

        arrDeletedMappings = deletedMappings;
      }

      this.aggregateBusinessunitChannels(data, arrDeletedMappings);

      await this.updateOrInsertBUChannelMappings();
    }, (errror)=>console.log(errror));

  }

  private async fetchDeletedBusinessunitChannelmappings(lastModifiedDate){
    let hourDifference;
    let now = new Date();
    let deletedRecords;
    let fetchXML = fetchQueries.deletedBusinessunitChannelMappings;
    hourDifference = this.dateTimeFormatsService.getDifferenceInHours(
      now,
      new Date(lastModifiedDate)
    )
    //add one to make sure we take care of fractional difference in hours
    hourDifference += 1
    fetchXML = fetchXML.replace('{hourDifference}',hourDifference);
    await this.dynamics.executeFetchQuery('indskr_trackchanges', fetchXML).then((data)=>{
      deletedRecords = data;
    })
    return deletedRecords;
  }

  public aggregateBusinessunitChannels(rawData: any, arrDeletedMappings) {

    if (!isEmpty(rawData)) {

      rawData.map((businessUnitChannel) => {

        if (businessUnitChannel && businessUnitChannel.indskr_businessunitchannelid) {

          let index = this.businessunitChannels.findIndex(o => o.indskr_businessunitchannelid == businessUnitChannel['indskr_businessunitchannelid']);

          let buChannel = new BusinessUnitChannel(businessUnitChannel);

          if (index >= 0) {
            this.businessunitChannels[index] = buChannel;
          } else {
            this.businessunitChannels.push(buChannel);
          }
        }
      });

      arrDeletedMappings.map((o) => {

        let foundIndex = this.businessunitChannels.findIndex(item => item.indskr_businessunitchannelid === o.indskr_businessunitchannelid);
        if (foundIndex >= 0)this.businessunitChannels.splice(foundIndex, 1);
      });

    }
  }

  private updateOrInsertBUChannelMappings() {
    this.disk.updateOrInsert(DB_KEY_PREFIXES.BUSINESS_UNIT_CHANNELS, (doc) => {
      if (!doc || !doc.raw) {
        doc = {
          raw: []
        };
      }
      doc.raw = this.businessunitChannels;
      doc.lastUpdatedTime = new Date().getTime();
      return doc;
    });
  }

  private async getAllActiveChannels() {
    const syncState = await this.disk.getSyncState(
      DB_SYNC_STATE_KEYS.SYNC_CONSENT_CHANNELS
    );
    const isInitialSync = !syncState || !syncState.lastUpdatedTime;
    const newLastUpdatedTime = new Date().getTime();
    const consentChannelSyncInfo: EntitySyncInfo = {
      entityName: EntityNames.channel,
      totalFailed: 0,
      totalSynced: 0,
      errors: [],
      syncStatus: true
    };
    await this.consentDataService
      .getConsentChannels(isInitialSync, syncState.lastUpdatedTime)
      .then(async (data: Channel[]) => {
        console.log("Saving active consents to disk...");
        //OMNI-1519
        if(data && data.length > 0) data = data.filter(dt => (dt.indskr_displayasconsentchannel && dt.indskr_displayasconsentchannel === true) || (dt.indskr_externalchannel && dt.indskr_externalchannel === true));
        this.savedChannels = data;
        await this.saveConsentChannelsToDisk(
          isInitialSync,
          DB_KEY_PREFIXES.CHANNELS,
          data
        );
        await this.finalizeSyncState(syncState, consentChannelSyncInfo, data, isInitialSync, newLastUpdatedTime);
      })
      .catch(err => {
        this.handleSyncError(err, consentChannelSyncInfo, Endpoints.consent.GET_CONSENT_CHANNELS, "Error occurred while fetching active consent channels...");
      });
  }

  private async getAllConsentTerms() {
    const syncState = await this.disk.getSyncState(
      DB_SYNC_STATE_KEYS.SYNC_CONSENT_TERMS
    );
    const isInitialSync = !syncState || !syncState.lastUpdatedTime;
    const newLastUpdatedTime = new Date().getTime();
    const consentSyncInfo: EntitySyncInfo = {
      entityName: EntityNames.consentTerm,
      totalFailed: 0,
      totalSynced: 0,
      errors: [],
      syncStatus: true
    };
    await this.syncProfilerService.timeAsync('consents_terms_api',
      () => this.consentDataService.getConsentTerms(isInitialSync, syncState.lastUpdatedTime))
      .then(data => data || [])
      .then(async (data: ConsentTerm[]) => {
        await this.syncProfilerService.timeAsync('consents_terms_processing', async () => {
          this.processMappingsAndExtractTopics(data);
          await this.updateTermChannels(data);
          await this.saveConsentTermsToDisk(
            isInitialSync,
            DB_KEY_PREFIXES.CONSENT_TERMS,
            data
          );
          await this.loadConsentTermsFromDB();
          await this.finalizeSyncState(syncState, consentSyncInfo, data, isInitialSync, newLastUpdatedTime);
        });
      })
      .catch(err => {
        this.handleSyncError(err, consentSyncInfo, Endpoints.consent.GET_ACTIVE_CONSENTS, "Error occurred while fetching active consents...");
      });
  }

  private processMappingsAndExtractTopics(consentTerms: ConsentTerm[]): void {
    if (!Array.isArray(consentTerms)) {
      return;
    }
    consentTerms.forEach(term => {
      if (term.mappings && Array.isArray(term.mappings) && term.mappings.length > 0) {
        // Create a Map to store unique topics by topic ID
        const uniqueTopicsMap = new Map<string, Topic>();

        term.mappings.forEach(mapping => {
          if (mapping.topic && mapping.topicName) {
            // Only add if topic ID doesn't exist in the map
            if (!uniqueTopicsMap.has(mapping.topic)) {
              uniqueTopicsMap.set(mapping.topic, new Topic(
                mapping.topic,
                mapping.topicName,
                true, // isChecked default
                null, // optional consent ID
                mapping.purpose,
                mapping.complianceProfile
              ));
            }
          }
        });
        term.topics = Array.from(uniqueTopicsMap.values());
      } else {
        term.topics = [];
      }
    });
  }

  private async finalizeSyncState(syncState: any, syncInfo: EntitySyncInfo, data: any, isInitialSync: boolean, newLastUpdatedTime: number): Promise<void> {
    syncState.lastUpdatedTime = newLastUpdatedTime;
    await this.disk.updateSyncState(syncState);
    if (Array.isArray(data)) {
      syncInfo.totalSynced = data.length;
    }
    this.deltaService.addEntitySyncInfo(syncInfo);
    console.log(`Sync status : ${JSON.stringify(syncState)} initial sync : ${isInitialSync}`);
  }

  private handleSyncError(err: any, syncInfo: EntitySyncInfo, endpoint: string, message: string): void {
    console.error(message, err);
    this.deltaService.addSyncErrorToEntitySyncInfo(
      syncInfo,
      this.authService.userConfig.activeInstance.entryPointUrl + endpoint,
      err
    );
    this.deltaService.addEntitySyncInfo(syncInfo);
  }

  private async loadAndRefreshActiveConsents(): Promise<void> {
    await this.disk
      .batchFetch(DB_ALLDOCS_QUERY_OPTIONS.GET_ALL_ACTIVE_CONSENTS)
      .then((data: ContactConsent[]) => {
        if (data) {
          data.forEach(cct => {
            if (Array.isArray(cct.activeConsents)) {
              cct.activeConsents.forEach(term => {
                term.isOfflineSaved = false;
                term.isSelectedTerm = false;
              });
            }
          });
        }
        this.allActiveConsentSubject.next(data);
        // Mark the cold-start cache as satisfied — this read is equivalent to ensureActiveConsentsLoadedFromDisk
        // and more recent. Without this, a concurrent ensureActiveConsentsLoadedFromDisk call (e.g. from
        // getExpiringConsentSuggestions) would overwrite the subject with a stale disk snapshot.
        if (!this.activeConsentsLoadedFromDiskPromise) {
          this.activeConsentsLoadedFromDiskPromise = Promise.resolve();
        }
        console.log(`active consents from disk : ${data ? data.length : 0}`);
      });
  }

  private saveConsentActivitiesToDisk(): Promise<any> {
    return this.disk.updateOrInsert(DB_KEY_PREFIXES.CONSENT_ACTIVITIES, (doc) => {
      doc = {
        raw: this.allConsentActivitiesWithProductsAndEmails,
        lastDeltaSync: new Date().getTime()
      };
      return doc;
    });
  }

  public async getAllConsentActivitiesForProductsAndEmails(isFullSync, isGlobalSearchAddedContact = false, contactId: string = '') {
    console.log(`getAllConsentActivitiesForProductsAndEmails`);

    let offlineDocs;
    let lastModifiedForDeltaSync, hourDifference;
    let now = new Date();

    await this.disk.retrieve(DB_KEY_PREFIXES.CONSENT_ACTIVITIES, true).then((doc)=>{
        offlineDocs = doc;
        if(doc && doc.raw){
          this.allConsentActivitiesWithProductsAndEmails = doc.raw
        }
        else {
          this.allConsentActivitiesWithProductsAndEmails = [];
          isFullSync = true;
        }
    });

      let fetchXML = fetchQueries.fetchAllConsentActivitiesForProductsAndEmails;
      
      // Check if RTM fetch is enabled
      const fetchRTM = this.authService.hasIoConfigWithFeatureCode(IoConfigurationFeatureCodes.BU_CONSENT_GA_FETCHRTM) as boolean;
      const rtmAttributes = fetchRTM 
        ? '<attribute name="indskr_topic"/>\n    <attribute name="indskr_purpose"/>'
        : '';
      fetchXML = fetchXML.replace('{rtmAttributes}', rtmAttributes);
      
      let positionIds = this.authService.user.positions.map(o => {
        return o.ID
      });
      let positionString = '';
      positionIds.forEach(p => positionString += '<value>' + p + '</value>');
      fetchXML = fetchXML.replace('{userPosition}', positionString)
                        .replace('{parentUserPosition}', positionString);
      if(isFullSync) {
        fetchXML = fetchXML.replace('{contactFilter}', '');
        fetchXML = fetchXML.replace('{deltaSyncFilterPosition}', '');
        fetchXML = fetchXML.replace('{deltaSyncFilter}', '');
      }
      else {
        let deltaSyncFilter = `
          <filter type="or">
            <condition entityname="indskr_consentactivity" attribute="modifiedon" operator="last-x-hours" value="{lastXHours}" />
            <condition entityname="indskr_email_address" attribute="modifiedon" operator="last-x-hours" value="{lastXHours}" />
          </filter> 
        `;

        if (contactId && !isEmpty(contactId)) {
          let contactFilter = `
            <filter type="and">
              <condition attribute="contactid" operator="eq" value="${contactId}" />
            </filter> 
          `;
          fetchXML = fetchXML.replace('{contactFilter}', contactFilter);
        } else {
          fetchXML = fetchXML.replace('{contactFilter}', '');
        }

        if (isGlobalSearchAddedContact) {

          deltaSyncFilter = `
            <filter type="or">
              <condition attribute="modifiedon" operator="last-x-hours" value="{lastXHours}" />
            </filter> 
          `;

          hourDifference = 1;
          deltaSyncFilter = deltaSyncFilter.replace(/{lastXHours}/g, hourDifference);
          fetchXML = fetchXML.replace('{deltaSyncFilterPosition}', deltaSyncFilter);
          fetchXML = fetchXML.replace('{deltaSyncFilter}', '');
        } else { 

          lastModifiedForDeltaSync = offlineDocs ? offlineDocs.lastDeltaSync : null;

          if(lastModifiedForDeltaSync){
            hourDifference = this.dateTimeFormatsService.getDifferenceInHours(
              now,
              new Date(lastModifiedForDeltaSync)
            )
            //add one to make sure we take care of fractional difference in hours
            hourDifference += 1;
            deltaSyncFilter = deltaSyncFilter.replace(/{lastXHours}/g, hourDifference);

          } else { 
            deltaSyncFilter = ''
          }

          fetchXML = fetchXML.replace('{deltaSyncFilterPosition}', '');
          fetchXML = fetchXML.replace('{deltaSyncFilter}', deltaSyncFilter);
        }
      }

      try {
        let response = await this.syncProfilerService.timeAsync('consents_activities_api',
          () => this.dynamics.executeFetchQuery('indskr_consentactivities', fetchXML));
        console.warn(`getAllConsentActivitiesForProductsAndEmails: executeFetchQuery`);

        if(!isEmpty(response)){
          await this.syncProfilerService.timeAsync('consents_activities_processing', async () => {
            // Base fields to pick
            const fieldsToPick = ['consentActivityId','offlineId', 'contactId','consentTypeId','consentTypeId@OData.Community.Display.V1.FormattedValue','indskr_type','indskr_optinsource', 'createdon','indskr_activitydate', '_createdby_value', 'indskr_name',
              'consentTermId', 'consentTermName', 'emailAddress', 'emailAddressStatecode', 'emailAddressId', 'indskr_productname','indskr_phone','indskr_addressname','indskr_consentactivitysourcetype','consentSourceName',
              'indskr_optinsource_Formatted', 'indskr_optoutsource_Formatted'];

            // Add RTM fields if enabled
            if (fetchRTM) {
              fieldsToPick.push('_indskr_topic_value@OData.Community.Display.V1.FormattedValue', '_indskr_topic_value');
            }

            const filtered: any[] = await this.syncProfilerService.timeAsync('consents_activities_field_pick',
              async () => response.map(item => pick(item, fieldsToPick)));

            await this.syncProfilerService.timeAsync('consents_activities_merge', async () => {
              if (!isEmpty(this.allConsentActivitiesWithProductsAndEmails)) {
                // Fix 7: Map-based O(n) merge instead of O(n²) findIndex per item
                const indexMap = new Map(
                  this.allConsentActivitiesWithProductsAndEmails.map((e, i) => [String(e['consentActivityId']), i])
                );
                for (const item of filtered) {
                  const key = String(item['consentActivityId']);
                  const idx = indexMap.get(key);
                  if (idx !== undefined) {
                    this.allConsentActivitiesWithProductsAndEmails[idx] = item;
                  } else {
                    indexMap.set(key, this.allConsentActivitiesWithProductsAndEmails.length);
                    this.allConsentActivitiesWithProductsAndEmails.push(item);
                  }
                }
              }
              else {
                this.allConsentActivitiesWithProductsAndEmails = filtered;
              }
            });

            await this.syncProfilerService.timeAsync('consents_activities_db_write', async () =>
              this.saveConsentActivitiesToDisk()
            );
          });
        }
      } catch(error) {
        console.log("Error fetching all consent activities with products and emails: ", error)
      }
  }

  public async getProductsWithConsentsByContactId() {
    if (
      this.deviceService.isOffline
      || !this.contactService.contactInformation
    ) return;

    console.log(`getProductsWithConsentsByContactId`);

    let offlineDocs;
    let lastModifiedForDeltaSync, hourDifference;
    let now = new Date();

    await this.disk.retrieve(DB_KEY_PREFIXES.CONSENT_ACTIVITIES, true).then((doc)=>{
      offlineDocs = doc;
    });

    let fetchXML = fetchQueries.consentActivitiesByContactId;
    fetchXML = fetchXML.replace('{contactId}', `${this.contactService.contactInformation.ID.toString()}`);

    lastModifiedForDeltaSync = offlineDocs ? offlineDocs.lastDeltaSync : null;
    if(lastModifiedForDeltaSync){
      hourDifference = this.dateTimeFormatsService.getDifferenceInHours(
        now,
        new Date(lastModifiedForDeltaSync)
      )
      //add one to make sure we take care of fractional difference in hours
      hourDifference += 1;
      fetchXML = fetchXML.replace('{lastXHours}', hourDifference);
    } else
    fetchXML = fetchXML.replace('{lastXHours}', "3");

    try {
      let response = await this.dynamics.executeFetchQuery('indskr_consentactivities', fetchXML);
      console.warn(`getProductsWithConsentsByContactId: executeFetchQuery`);

      if(!isEmpty(response)){
        const filtered = response.map(item => pick(item, ['consentActivityId','contactId',
        'consentTermId', 'emailAddress', 'indskr_productname']));

        if (!isEmpty(this.allConsentActivitiesWithProductsAndEmails)) {
          await filtered.forEach(async (item) => {
            let existingConsentActivity = this.allConsentActivitiesWithProductsAndEmails.find(element => element['consentActivityId'] == item['consentActivityId']);

            if(!existingConsentActivity) {
              this.allConsentActivitiesWithProductsAndEmails.push(item);
            }
          })
        }
        else {
          this.allConsentActivitiesWithProductsAndEmails = filtered;
        }

        this.saveConsentActivitiesToDisk();
      }
    } catch(error) {
      console.log("Error fetching consent activities by contactId: ", error)
    }
  }

  public async getConsentsByContactIdAndConsentTypes(contactId: string, consentTypes = []) {
    console.log(`getConsentsByContactIdAndConsentTypes`);
    let fetchXML = fetchQueries.fetchConsentsByContactIdAndTypes;
    fetchXML = fetchXML.replace('{contactId}', contactId);
    let consentTypeFilter = '';
    consentTypes.forEach(ct => { consentTypeFilter += '<value>' + ct + '</value>' });
    fetchXML = fetchXML.replace('{consentTypes}', consentTypeFilter);
    try {
      const consents = await this.dynamics.executeFetchQuery('indskr_consents', fetchXML);
      return consents;
    } catch (error) {
      console.log("Error fetching consent activities by contactId: ", error)
    }
  }

  private async getAllConsentTermsForAllocationOrders() {
    const syncState = await this.disk.getSyncState(
      DB_SYNC_STATE_KEYS.SYNC_CONSENT_TERMS_ALLOCATION_ORDERS
    );
    const isInitialSync = !syncState || !syncState.lastUpdatedTime;
    const newLastUpdatedTime = new Date().getTime();
    const consentSyncInfo: EntitySyncInfo = {
      entityName: EntityNames.consentTerm,
      totalFailed: 0,
      totalSynced: 0,
      errors: [],
      syncStatus: true
    };
    await this.consentDataService
      .getConsentTermsForAllocationOrders(isInitialSync, syncState.lastUpdatedTime)
      .then(data => data || [])
      .then(async (data: ConsentTerm[]) => {
        console.log("Saving active consents for allocation orders to disk...");
        // this.updateTermChannels(data);
        await this.saveConsentTermsToDisk(
          isInitialSync,
          DB_KEY_PREFIXES.CONSENT_TERMS_ALLOCATION_ORDERS,
          data
        );
        await this.finalizeSyncState(syncState, consentSyncInfo, data, isInitialSync, newLastUpdatedTime);
      })
      .catch(err => {
        this.handleSyncError(err, consentSyncInfo, Endpoints.consent.GET_ACTIVE_CONSENTS, "Error occurred while fetching active consents for allocation orders...");
      });
  }

  async updateTermChannels(data: ConsentTerm[]) {
    if (!this.savedChannels || this.savedChannels.length == 0) {
      try {
        await this.disk
        .batchFetch(DB_ALLDOCS_QUERY_OPTIONS.GET_ALL_CONSENT_CHANNELS)
        .then((data: Channel[]) => {
          this.allConsentChannelSubject.next(data);
          this.savedChannels = data;
          console.log(
            `updateTermChannels(): active consent channels from disk : ${data ? data.length : 0}`
          );
        });
      } catch (error) {
        console.error('Failed to fetch consent channels from disk:', error);
      }
    }

    data.forEach((term: ConsentTerm) => {
      let channels:Channel[] = [];
      if (term.channels) {
        term.channels.forEach(tch => {
          let ch: Channel = this.savedChannels.find(sch => sch.indskr_consenttypeid === tch.indskr_consenttypeid);
          if (ch) channels.push(ch);
        });
      }
      term.channels = channels;
    });
  }

  /**
   * Converts a consent date field (createdon / indskr_fromdate / indskr_untildate) to a
   * millisecond timestamp regardless of how the API or PouchDB serialized it.
   *
   * Handles:
   *  - JS number           → returned as-is
   *  - Numeric string      → parseFloat  (e.g. "1750291200000")
   *  - ISO date string     → new Date()  (e.g. "2026-06-25T00:00:00.000Z")
   *
   * NOTE: parseFloat("2026-06-25T...") returns 2026 (the year digits) — NOT NaN — so we must
   * detect ISO strings via regex BEFORE calling parseFloat, not after.
   */
  private parseConsentTimestamp(val: string | number | null | undefined): number {
    if (val == null) return NaN;
    if (typeof val === 'number') return val;
    const str = String(val);
    // All-digit string (with optional decimal) → numeric timestamp
    return /^\d+(\.\d+)?$/.test(str) ? parseFloat(str) : new Date(str).getTime();
  }

  private _filterActiveConsents(activeConsents: ConsentTerm[], deletedConsents: any) {
    // Create a Set of deleted consent IDs for efficient lookup
    const deletedConsentIDs = new Set<string>();
    deletedConsents.forEach(deletedConsent => {
      // The FetchXML alias for indskr_entityid is 'indskr_activeConsent', not 'indskr_consentid'.
      // Use nullish coalescing so whichever field the API returns is used.
      const consentID = deletedConsent['indskr_consentid'] ?? deletedConsent['indskr_activeConsent'];
      if (consentID) {
        deletedConsentIDs.add(consentID.toString());
      }
    });
    // Iterate backwards to safely remove items
    for (let i = activeConsents.length - 1; i >= 0; i--) {
      const activeConsent = activeConsents[i];

      // Filter consentActivity array if it exists
      if (activeConsent.consentActivity && Array.isArray(activeConsent.consentActivity)) {
        activeConsent.consentActivity = activeConsent.consentActivity.filter(activity => {
          // Keep the activity if it doesn't have indskr_consentid or if it's not in the deleted list
          return !activity.indskr_consentid || !deletedConsentIDs.has(activity.indskr_consentid.toString());
        });

        // If consentActivity array is now empty, remove the entire activeConsent
        if (activeConsent.consentActivity.length === 0) {
          activeConsents.splice(i, 1);
        }
      }
    }

    return cloneDeep(activeConsents)
  }


  private saveConsentsToDisk(isInitialSync, dbKey, data: ContactConsent[], deletedConsents?: any) {
    if (Array.isArray(data) && data.length > 0) {
      data.forEach((consent: ContactConsent) => {
        consent._id = dbKey + consent.indskr_contactid;
        if (Array.isArray(consent.activeConsents) && consent.activeConsents.length > 0) {

          if (deletedConsents && isArray(deletedConsents) && !isEmpty(deletedConsents)){
            consent.activeConsents = this._filterActiveConsents(consent.activeConsents, deletedConsents)
          }

          consent.activeConsents.forEach((term: ConsentTerm) => {

            // Delta API can return dates as ISO strings (e.g. "2026-06-25T00:00:00.000Z").
            // parseFloat("2026-06-25T...") = 2026 (year digits, not a timestamp) — not NaN —
            // so isTermExpired() would receive an epoch-near value and return false (expired),
            // causing the splice to fire and remove the valid term from disk.
            // parseConsentTimestamp() uses a regex to detect ISO strings before parseFloat.
            term.createdon = this.parseConsentTimestamp(term.createdon);
            term.indskr_fromdate = this.parseConsentTimestamp(term.indskr_fromdate);
            term.indskr_untildate = this.parseConsentTimestamp(term.indskr_untildate);
            term.isSavedConsent = true;
            term.isAcceptedTerms = true;
            term.isExpanded = false;
            if (term.products && term.products.length) {
              term.products.forEach(product => {
                product.isChecked = true;
              });
            }
            if (term.channels && term.channels.length) {
              term.channels.forEach(channel => {
                channel.isChecked = true;
                if (channel.values.length) {
                  channel.values.forEach(value => {
                    value.isChecked = true;
                  });
                }
              });
            }

            const isTermValid = this.isTermExpired(term);
            const datesAreParsed = !isNaN(term.indskr_fromdate) && !isNaN(term.indskr_untildate);
            // Only splice if explicitly deleted, or dates are valid and term is confirmed expired.
            // Terms with unparseable dates (NaN) are preserved — the contact-level merge handles them.
            if (term.track_action === TrackAction.Deleted || (datesAreParsed && !isTermValid)) {
              this.disk.retrieve(consent._id.toString()).then((conConsent: ContactConsent) => {
                if (conConsent) {
                  let index = conConsent.activeConsents.findIndex(
                    (cT: ConsentTerm) => {
                      return cT.indskr_consenttermsid === term.indskr_consenttermsid;
                    });
                  if (index !== -1) {
                    conConsent.activeConsents.splice(index, 1);
                    this.disk.updateDocWithIdAndRev(conConsent);
                  }
                }
              });
            }
            else if (term.track_action === TrackAction.Download) {
              this.disk.retrieve(consent._id.toString()).then((conConsent: ContactConsent) => {
                if (conConsent) {
                  let index = conConsent.activeConsents.findIndex(
                    (cT: ConsentTerm) => {
                      return cT.indskr_consenttermsid === term.indskr_consenttermsid;
                    });
                  if (index === -1) {
                    conConsent.activeConsents.push(term);
                    this.disk.updateDocWithIdAndRev(conConsent);
                  }
                }
              });
            }
          });
        }
        if (!isInitialSync) {
            this.disk.retrieve(consent._id.toString()).then((conConsent: ContactConsent) => {
                if(!conConsent) {
                  this.disk.updateOrInsert(consent._id.toString(), doc => consent)
                    .catch(error => console.error('saveConsentsToDisk: ', error));
                }
                else {
                  if(conConsent.activeConsents && consent.activeConsents) {
                    let filteredConsents: ConsentTerm[];
                    // Add existing consent activities & channels to delta consent
                    consent.activeConsents.forEach((deltaConsent: ConsentTerm) => {
                      const existingConsent = conConsent.activeConsents.find((con) => con.indskr_consenttermsid == deltaConsent.indskr_consenttermsid);
                      let existingConsentActivities = existingConsent?.consentActivity;
                      let exisitngConsentChannels = existingConsent?.channels;
                      let existingSignature = existingConsent?.indskr_signature;
                      if(!isEmpty(existingConsentActivities)) {
                        if(!isEmpty(deltaConsent.consentActivity)) {
                          let newValues = existingConsentActivities.filter(ea => !deltaConsent.consentActivity.some(da => ea.indskr_consentactivityid == da.indskr_consentactivityid));
                          deltaConsent.consentActivity.push(...newValues);
                        }
                        else deltaConsent.consentActivity = existingConsentActivities;
                      }
                      if(!isEmpty(exisitngConsentChannels)) {
                        if(!isEmpty(deltaConsent.channels)) {
                          exisitngConsentChannels.forEach(eCh => {
                            deltaConsent.channels.forEach((dCh, index) => {
                              if(eCh.indskr_consenttypeid == dCh.indskr_consenttypeid) {
                                if(!isEmpty(eCh.values)) {
                                  let newValues = dCh.values.filter(dc => !eCh.values.some(ec => ec.value == dc.value));
                                  if(!isEmpty(newValues))eCh.values.push(...newValues);
                                } else eCh.values = dCh.values;
                                deltaConsent.channels.splice(index,1);
                              }
                            })
                          })
                        if(!isEmpty(deltaConsent.channels)) exisitngConsentChannels.push(...deltaConsent.channels);
                        }
                        deltaConsent.channels = exisitngConsentChannels;
                      }
                      if(!isEmpty(existingSignature) && isEmpty(deltaConsent.indskr_signature)) {
                        deltaConsent.indskr_signature = existingSignature;
                      }
                      // Removing the old consents in case of duplicate during delta
                      filteredConsents = conConsent.activeConsents.filter((existingConsent: ConsentTerm) => {
                        return existingConsent.indskr_consenttermsid !== deltaConsent.indskr_consenttermsid;
                      });
                      if(filteredConsents) {
                        // Pushing the latest data present in delta
                        filteredConsents.push(deltaConsent);
                        filteredConsents.forEach(con => {
                          con.channels.forEach(ch => {
                            delete ch._id;
                            delete ch._rev;
                          })
                        })
                        conConsent.activeConsents = filteredConsents;
                        this.disk.updateDocWithIdAndRev(conConsent);
                      }
                    });
                  }
                }
              }, err => {
                console.log("Error occurred" + err);
              }
          );
        }
      });
      if (isInitialSync) {
        this.disk.bulk(data);
      }
    }
  }

  isTermExpired(cntTerm: ConsentTerm) {
    let isValid = false;
    const sod = parseFloat(cntTerm.indskr_fromdate.toString());
    const eod = parseFloat(cntTerm.indskr_untildate.toString());
    const now = new Date();

    if (isNaN(sod) || isNaN(eod) || this.dateTimeFormatsService.isPastDate(eod)) {}
    else {
      isValid = this.dateTimeFormatsService.isDateWithinRange(now, this.dateTimeFormatsService.getStartOfDay(sod), this.dateTimeFormatsService.getEndOfDay(eod))
    }

    return isValid;
  }

  private async saveConsentChannelsToDisk(isInitialSync, dbKey, data: Channel[]) {
    if (data && data.length >= 0) {
      let diskCh = await this.disk.batchFetch(DB_ALLDOCS_QUERY_OPTIONS.GET_ALL_CONSENT_CHANNELS)
      if(diskCh && Array.isArray(diskCh)){
        diskCh.forEach((ch: Channel) => {
          if (ch) {
            let index = data.findIndex(
              (c: Channel) => {
                return c.indskr_consenttypeid === ch.indskr_consenttypeid;
              });
            if (index === -1) {
              this.disk.remove(ch._id.toString());
            }
          }
        });
      }
      data.forEach((channel: Channel) => {
        channel._id = dbKey + channel.indskr_consenttypeid;
        if (!isInitialSync) {
          this.disk.retrieve(channel._id.toString()).then(
            exist => {
              // if (!exist) {
              this.disk.updateOrInsert(channel._id.toString(), doc => channel);
            },
            err => {
              console.log("Error occurred" + err);
            }
          );
        }
      });
      if (isInitialSync) {
        this.disk.bulk(data);
      }
      console.log(
        `${data.length} active consent channels has been written to disk`
      );
    }
  }

  private saveConsentTermsToDisk(isInitialSync, dbKey, data: ConsentTerm[]) {
    if (data && data.length > 0) {
      data.forEach( async (consent: ConsentTerm) => {
        consent._id = dbKey + consent.indskr_consenttermsid;
        if (consent.track_action == TrackAction.Deleted || !this.isTermExpired(consent)) {
          this.disk.retrieve(consent._id.toString()).then(async (exist) => {
            if (exist)
              await this.disk.remove(consent._id.toString());
          });
        } else {
          if (!isInitialSync) {
            let exist = await this.disk.retrieve(consent._id.toString()); //.then(
            //async exist => {
            if (!exist) {
              await this.disk.updateOrInsert(consent._id.toString(), doc => consent)
                .catch(error => console.error('saveConsentTermsToDisk: ', error));
            } else {
              if (dbKey === DB_KEY_PREFIXES.CONSENT_TERMS || dbKey === DB_KEY_PREFIXES.CONSENT_TERMS_ALLOCATION_ORDERS) {
                exist.indskr_consentTermName = consent.indskr_consentTermName;
                exist.indskr_fromdate = consent.indskr_fromdate;
                exist.indskr_untildate = consent.indskr_untildate;
                exist.indskr_alwaysrequired = consent.indskr_alwaysrequired;
                exist.indskr_body = consent.indskr_body;
                exist.indskr_signaturerequired = consent.indskr_signaturerequired;
                exist.indskr_recaptureconsent = consent.indskr_recaptureconsent;
                exist.indskr_confirmationmessage = consent.indskr_confirmationmessage;
                exist.indskr_agreementmessage = consent.indskr_agreementmessage;
                exist.products = consent.products;
                exist.channels = consent.channels;
                exist.topics = consent.topics;
                exist.channels.forEach(ch => {
                  delete ch._id;
                  delete ch._rev;
                })
                exist.indskr_allowpaperconsentcapture = consent.indskr_allowpaperconsentcapture || false;
                await this.disk.updateOrInsert(consent._id.toString(), doc => exist);

                // Update Contact Consent as well since delta sync doesn't catch this...
                const contactConsents = this.allActiveConsentSubject.getValue();
                for (let i = 0; i < contactConsents.length; i++) {
                  const contactConsent = contactConsents[i];
                  if (Array.isArray(contactConsent.activeConsents) && contactConsent.activeConsents.some(c => c.indskr_consenttermsid === consent.indskr_consenttermsid)) {
                    const contactConsentDoc = await this.disk.retrieve(DB_KEY_PREFIXES.CONSENTS + contactConsent.indskr_contactid, true);
                    if (contactConsentDoc && Array.isArray(contactConsentDoc.activeConsents)) {
                      const idx = contactConsentDoc.activeConsents.findIndex(c => c.indskr_consenttermsid === consent.indskr_consenttermsid);
                      if (idx >= 0) {
                        contactConsentDoc.activeConsents[idx].indskr_consentTermName = consent.indskr_consentTermName;
                        contactConsentDoc.activeConsents[idx].indskr_fromdate = consent.indskr_fromdate;
                        contactConsentDoc.activeConsents[idx].indskr_untildate = consent.indskr_untildate;
                        contactConsentDoc.activeConsents[idx].products = consent.products;
                        const existingChannels: Channel[] = cloneDeep(contactConsentDoc.activeConsents[idx].channels);
                        contactConsentDoc.activeConsents[idx].channels = consent.channels;
                        contactConsentDoc.activeConsents[idx].channels.forEach((ch: Channel) => {
                          const existingDataIndex = existingChannels.findIndex(exCh => exCh.indskr_consenttypeid === ch.indskr_consenttypeid);
                          if (existingDataIndex >= 0) {
                            delete ch._id;
                            delete ch._rev;
                            ch.values = existingChannels[existingDataIndex].values;
                          }
                        })
                        await this.disk.updateDocWithIdAndRev(contactConsentDoc);
                      }
                    }
                  }
                }
              }
            }
          }
        }
      });
      if (isInitialSync) {
        this.disk.bulk(data);
      }
      console.log(
        `${data.length} active consent terms has been written to disk`
      );
    }
  }

  public async createOptOutConsentActivity(consentActivities) {
    if (this.deviceService.isOffline) return;
    return await this.consentDataService.saveOptOutConsentActivity(consentActivities).then(
      async (res) => {
        const contactId = consentActivities[0]["indskr_Contact@odata.bind"];
        let dbKey = DB_KEY_PREFIXES.CONSENTS + contactId;
        return Promise.all([
          this.disk.retrieve(dbKey.toString()).then(async (exist) => {
            if (exist)
              this.disk.remove(dbKey.toString());
          })
        ]).then(async () => {
          await this.getAllActiveConsentsByContactId(contactId).then(async (res)=> {
            await this.disk
              .batchFetch(DB_ALLDOCS_QUERY_OPTIONS.GET_ALL_ACTIVE_CONSENTS)
              .then((data: ContactConsent[]) => {
                if (Array.isArray(data)) {
                  data.forEach(cct => {
                    if (Array.isArray(cct.activeConsents)) {
                      cct.activeConsents.forEach(term => {
                        term.isOfflineSaved = false;
                        term.isSelectedTerm = false;
                      });
                    }
                  });
                }
                this.allActiveConsentSubject.next(data);
                // console.log(`active consents from disk : ${data ? data.length : 0}`);
                
                // Delete email addresses after consent data is refreshed
                // This ensures we check against the latest consent state
                if (this.authService.user.enableMaskingOfEmailOnOptOut) {
                  this.updateEmailAddressForContact(consentActivities);
                }
              });
          });
          // Need to update the feeb tab view for consent activities
          await this.getAllConsentActivitiesForProductsAndEmails(false);
          this.notificationService.notify(
            this.translate.instant('CONSENT_OPTED_OUT_SUCCESSFULLY'),
            "Consent Service",
            "top",
            ToastStyle.INFO
          );
        })
      }
    )
  }

  public async saveOrUpdateActivityInDB(consentActivities: ConsentActivity[]) {
    if(!isEmpty(consentActivities)) {
      consentActivities.forEach(async consentActivity => {
        let _id = DB_KEY_PREFIXES.CONSENTS + consentActivity.contactId;
        let consent: ContactConsent = await this.disk.retrieve(_id);
        if(!isEmpty(consent)) {
          consent.activeConsents.find(con => con.indskr_consenttermsid == consentActivity.consentTermId)
          .consentActivity.push(consentActivity)
        }
        await this.disk.updateDocWithIdAndRev(consent);
        let index = this.allActiveConsentSubject.value.findIndex(
          (c: ContactConsent) => {
            return c.indskr_contactid === consent.indskr_contactid;
          }
        );
        Object.assign(this.allActiveConsentSubject.value[index], consent);
        this.allActiveConsentSubject.next(this.allActiveConsentSubject.value);
      })

    }
  }

  public async createConsentTerm(term: ConsentTerm, paperConsent?) {
    term.offlineId = DB_KEY_PREFIXES.CONSENTS + new Date().getTime();
    if (!this.deviceService.isOffline) {
      try {
        await this.consentDataService.saveConsent(term).then(
          async (res: ConsentTerm) => {
            term.isSavedConsent = true;
            this.notificationService.notify(
              this.translate.instant('CONSENT_SUBMITTED_SUCCESSFULLY'),
              "Consent Service",
              "top",
              ToastStyle.INFO
            );
            Promise.all([
              await this.getAllConsentActivitiesForProductsAndEmails(false, false, term?.indskr_contactid ?? null).then(async() => {
              if(!isEmpty(paperConsent)) {
                let notes = [];
                let consentActivities = this.allConsentActivitiesWithProductsAndEmails.filter(con => con.offlineId == term.offlineId);
                if(!isEmpty(consentActivities) && isArray(consentActivities)) {
                  consentActivities.forEach(activity => {
                    let note = cloneDeep(paperConsent);
                    note.consentActivityId = activity.consentActivityId;
                    notes.push(note);
                  })
                }
                await this.consentDataService.savePaperConsent(notes);
              }
            })
            ]).then(async () => {
              let _id = DB_KEY_PREFIXES.CONSENTS + term.indskr_contactid;
              const consent: ContactConsent = await this.disk.retrieve(_id);
              if (!consent) {
                const newConsent: ContactConsent = new ContactConsent("", "", "",[]);
                newConsent._id = _id;
                newConsent.indskr_contactid = term.indskr_contactid;
                newConsent.activeConsents.push(term);
                await this.disk.updateOrInsert(_id.toString(), doc => newConsent)
                  .catch(error => console.error('saveOrUpdateConsentsInDB: ', error));
              }
              this.getAllActiveConsents().then(async () => {
                await this.loadAndRefreshActiveConsents();
              })
            })
          });
      } catch(error) {
        console.error('createConsentTerm: ', error);
      }
    } else {
      term.isOfflineSaved = true;
      await this.saveOrUpdateConsentsInDB(term);
      // Track offline data count
      this.trackOfflineConsentDataCount();
    }
  }

  public async saveOrUpdateConsentsInDB(term: ConsentTerm) {
    let _id = DB_KEY_PREFIXES.CONSENTS + term.indskr_contactid;
    term.isSavedConsent = true;
    this.notificationService.notify(
      this.translate.instant('CONSENT_SUBMITTED_SUCCESSFULLY'),
      "Consent Service",
      "top",
      ToastStyle.INFO
    );
    const consent: ContactConsent = await this.disk.retrieve(_id);
      if (!consent) {
        const newConsent: ContactConsent = new ContactConsent("", "", "",[]);
        newConsent._id = _id;
        newConsent.indskr_contactid = term.indskr_contactid;
        newConsent.activeConsents.push(term);
        await this.disk.updateOrInsert(_id.toString(), doc => newConsent)
          .catch(error => console.error('saveOrUpdateConsentsInDB: ', error));
        let addedConsents: ContactConsent[] = [
          ...this.allActiveConsentSubject.value,
          newConsent
        ];
        this.allActiveConsentSubject.next(addedConsents);
        this.allActiveConsentSubject.next(this.allActiveConsentSubject.value);
      } else {
        consent.activeConsents.push(term);
        await this.disk.updateDocWithIdAndRev(consent);
        let index = this.allActiveConsentSubject.value.findIndex(
          (c: ContactConsent) => {
            return c.indskr_contactid === consent.indskr_contactid;
          }
        );
        Object.assign(this.allActiveConsentSubject.value[index], consent);
        this.allActiveConsentSubject.next(this.allActiveConsentSubject.value);
      }
      let index = this.allGeneratedTerms.value.findIndex(
        (t: ConsentTerm) => {
          return t.indskr_consenttermsid === term.indskr_consenttermsid;
        }
      );
      this.allGeneratedTerms.value[index].isSavedConsent = term.isSavedConsent;
      this.allGeneratedTerms.value[index].isOfflineSaved = term.isOfflineSaved;
      this.currentConsentTerm.next(term);
      this.allGeneratedTerms.next(this.allGeneratedTerms.value);

      this.showAllConsentsFilter$.next(true);
      await this.loadNextAvailableTermIfExist();
  }

  async loadNextAvailableTermIfExist() {
    const availableTerms: ConsentTerm[] = this.allGeneratedTerms.value.filter(term => !term.isSavedConsent);
    if (availableTerms.length) {
      availableTerms[0].isSelectedTerm = true;
      this.selectedConsentTerm.next(availableTerms[0]);
      this.allGeneratedTerms.value.forEach((generatedTerm: ConsentTerm) => {
        if (
          this.selectedConsentTerm.value.indskr_consenttermsid ===
          generatedTerm.indskr_consenttermsid
        ) {
          generatedTerm.isAcceptedTerms = false;
          generatedTerm.isSelectedTerm = true;
        } else {
          generatedTerm.isSelectedTerm = false;
        }
      });
    }
  }

  trackOfflineConsentDataCount() {
    const offlineDataCount = this.allActiveConsentSubject.value.reduce((acc, contactConsent) => {
      if (Array.isArray(contactConsent.activeConsents)) {
        acc += contactConsent.activeConsents.filter(c => c.isOfflineSaved === true).length;
      }
      return acc;
    }, 0);
    this.disk.setOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.CONSENT, offlineDataCount);
  }

  clearFilters() {
    this.showAllConsentsFilter = 'All';
    // this.isProductFilterExpanded = false;
    // this.isChannelFilterExpanded = false;
    this.consentChannelFilter$ = undefined;
    this.consentProductFilter$ = undefined;
    this.showAllConsentsFilter$.next(true);
  }

  /* Consent Related Offline Logic */
  /* =================================== */
  /* Used while performing the sync operation from master service */
  public async loadOfflineConsents(): Promise<any> {
    try {
      let offlineConsents: ConsentTerm[] = [];
      await this.disk
        .batchFetch(DB_ALLDOCS_QUERY_OPTIONS.GET_ALL_ACTIVE_CONSENTS)
        .then((data: ContactConsent[]) => {
          if (data.length) {
            data.forEach(contactConsent => {
              if (contactConsent.activeConsents && contactConsent.activeConsents.length) {
                offlineConsents = [
                  ...offlineConsents,
                  ...contactConsent.activeConsents.filter(
                    consent => consent.isOfflineSaved
                  )
                ];
              }
            });
          }
        });
      // Track offline data count
      this.disk.setOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.CONSENT, offlineConsents.length);
      return offlineConsents;
    } catch (diskError) {
      console.log("Disk error caught", diskError);
    }
  }

  public async updateOfflineConsents(res, offlineConsents, consentSyncInfo) {
    this.savedConsents = [];
    if (res.offlineConsents && Array.isArray(res.offlineConsents)) {
      this.savedConsents = res.offlineConsents.filter(oC => !oC.errorMessage);
      let uploadedOfflineDataCount = this.savedConsents.length;
      const failedConsents = res.offlineConsents.filter(oC => oC.errorMessage);
      if (this.savedConsents && Array.isArray(this.savedConsents) && this.savedConsents.length) {
        this.savedConsents.forEach(consent => {
          offlineConsents.forEach(oC => {
            if (oC.offlineId == consent.offlineId) {
              if (consent.track_action && consent.track_action == TrackAction.Deleted) {
                let index = this.savedConsents.findIndex(
                  cT => {
                    return cT.offlineId === consent.offlineId;
                  });
                if (index !== -1) {
                  this.savedConsents.splice(index, 1);
                }
                this.disk.retrieve(DB_KEY_PREFIXES.CONSENTS + oC.indskr_contactid)
                  .then((conConsent: ContactConsent) => {
                    let index = conConsent.activeConsents.findIndex(
                      (cT: ConsentTerm) => {
                        return cT.indskr_consenttermsid === oC.indskr_consenttermsid;
                      });
                    if (index !== -1) {
                      conConsent.activeConsents.splice(index, 1);
                      this.disk.updateDocWithIdAndRev(conConsent);
                    }
                  });
              }
              else {
                this.disk.retrieve(DB_KEY_PREFIXES.CONSENTS + oC.indskr_contactid)
                  .then((conConsent: ContactConsent) => {
                    const idx = Array.isArray(conConsent.activeConsents) ? conConsent.activeConsents.findIndex(ac => ac.offlineId === consent.offlineId) : -1;
                    if (idx >= 0) {
                      conConsent.activeConsents[idx].isOfflineSaved = false;
                      conConsent.activeConsents[idx].isSelectedTerm = false;
                      conConsent.activeConsents = this.mergeDuplicateTerms(conConsent.activeConsents);
                      this.disk.updateOrInsert(DB_KEY_PREFIXES.CONSENTS + oC.indskr_contactid, doc => conConsent);
                    }
                });
              }
            }
          });
        });

        this.loadAndMapConsentsMasterDataFromDB();
      }
      if (failedConsents && Array.isArray(failedConsents) && failedConsents.length) {
        failedConsents.forEach(consent => {
          this.deltaService.addSyncErrorToEntitySyncInfo(consentSyncInfo, this.authService.userConfig.activeInstance.entryPointUrl +
            Endpoints.offline.UPLOAD_MEETING, consent.errorMessage);
        });
      }
      // Track offline data count
      this.disk.setOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.CONSENT, offlineConsents.length - uploadedOfflineDataCount);

      await this.getAllActiveConsents();
      await this.getAllConsentActivitiesForProductsAndEmails(false);
    }
  }

  mergeDuplicateTerms(terms: ConsentTerm[]): ConsentTerm[] {
    //get grouped consents
    let groupedConsents = Object.entries(groupBy(terms, 'indskr_consenttermsid'))
      .map(([key, value]: [string, ConsentTerm[]]) => ({ termId: key, terms: value }));
    let uniqueTermSet = [];
    groupedConsents.forEach((consent) => {
      if (consent.terms.length > 1) {
        this.processDuplicates(consent.terms, uniqueTermSet);
      }
      else {
        uniqueTermSet.push(consent.terms[0]);
      }
    });
    return uniqueTermSet;
  }

  processDuplicates(terms: ConsentTerm[], uniqueTermSet: any[]) {
    let products = [];
    let channels = [];
    let uniqChannels = [];
    terms.forEach(term => {
      if (term.products) {
        products.push(...term.products);
      }
      if (term.channels) {
        channels.push(...term.channels);
      }
    });
    terms[0].products = uniqBy(products, 'indskr_productid');

    //get grouped channels
    let groupedChannels = Object.entries(groupBy(channels, 'indskr_consenttypeid'))
      .map(([key, value]: [string, Channel[]]) => ({ channelId: key, channels: value }));

    //process channel values
    groupedChannels.forEach((channel) => {
      if (channel.channels.length > 1) {
        let channelValues = [];
        channel.channels.forEach(ch => {
          channelValues.push(...ch.values);
        });
        channel.channels[0].values = uniqBy(channelValues, 'value');
        uniqChannels.push(channel.channels[0]);
      }
      else {
        uniqChannels.push(channel.channels[0]);
      }
    });

    terms[0].channels = uniqBy(uniqChannels, 'indskr_consenttypeid');
    uniqueTermSet.push(terms[0]);
  }

  public updateContact(contact: Contact, obj: any, type: ChannelActivityType, address?: ContactAddress): Promise<any> {
    return new Promise<any>((resolve, reject) => {
        const existingMobileNumber = contact.mobilePhone;
        let payload: any = {
          "indskr_externalid": contact.appId || '',
          "indskr_externalsource": contact.external_source || '',
          "contactid": contact.ID,
          "firstname": contact.firstName,
          "lastname": contact.lastName,
          "indskr_title": contact.indskr_title ? contact.indskr_title : null,
          "middlename": contact.middleName,
          "telephone1": contact.telephone,
          "mobilephone": type == ChannelActivityType.PHONE ? obj['value'] : existingMobileNumber,
          "indskr_lu_language": contact.primaryLanguage ? (contact.primaryLanguage.id || '')  : '',
          "indskr_lu_specialty": contact.primarySpecialty ? (contact.primarySpecialty.id || '')  : '',
          "indskr_speaker": contact.isSpeaker != 'No',
          "indskr_kol": contact.isKOL != 'No',
          "emails": type == ChannelActivityType.EMAIL ? [obj] : [],
          "addresses": type == ChannelActivityType.VISIT ? [obj] : [],
          "accounts": [],
          "indskr_alternatephone1": contact.indskr_alternatephone1 ? contact.indskr_alternatephone1 : '',
          "indskr_alternatephone2": contact.indskr_alternatephone2 ? contact.indskr_alternatephone2 : '',
          "indskr_alternatephone3": contact.indskr_alternatephone3 ? contact.indskr_alternatephone3 : '',
          "pendingPushOnDynamics": false
        };
        if (type == ChannelActivityType.PHONE) {
          if (existingMobileNumber) {
            payload[obj['field']] = existingMobileNumber;
          }
        }
        const payloadList = [payload];
      
        let loader
        this.loadingCtrl.create().then(async (load)=>{
          loader = load
          loader.present();
          // Online mode
          if (!this.deviceService.isOffline) {
            try {
              const res: any = await this.contactDataService.createContacts(payloadList);              
              if (type == ChannelActivityType.EMAIL) {
                const { emailObjs, updatedContactIdList } = this.extractEmailObjectsAndIds(res);
                if (!isEmpty(emailObjs)) {
                  emailObjs.forEach(emailObj => {
                    const contactId = emailObj.contactId;
                    const emailAddressId = emailObj.emailAddressId;
                    let foundContact = this.contactService.getContactByID(contactId);
                    if (foundContact) {
                      const existingEmailIndex = foundContact?.emailAddressList.findIndex(e => e.emailAddressId === emailAddressId);
                      if (existingEmailIndex < 0) {
                        foundContact.emailAddressList.push(emailObj);
                      }
                    }
                    this.contactService.contactInformation = this.contactService.getContactByID(contactId);

                    this.allGeneratedTerms.value.forEach(term => {
                      if (term.channels && term.channels.length > 0) {
                        term.channels.forEach(c => {
                          if (c.activityType === ChannelActivityType.EMAIL || c.indskr_consentType === ChannelType.FACEBOOK) {
                            c.values.push(
                              new ChannelValue(emailObj.emailAddressId, emailObj.emailAddress, !!c.isChecked)
                            );
                          }
                        });
                      }
                    });
                  });
                }
              }
              else if (type == ChannelActivityType.PHONE || type == ChannelActivityType.SMS || type == ChannelActivityType.WHATSAPP) {
                  this.contactService.contactInformation.mobilePhone = obj['value'];
                  if (existingMobileNumber) {
                    this.contactService.contactInformation[obj['field']] = existingMobileNumber;
                  }
                  this.allGeneratedTerms.value.forEach(term => {
                    if (term.channels && term.channels.length > 0) {
                      term.channels.forEach(c => {
                        if ((c.activityType == ChannelActivityType.PHONE && c.indskr_consentType == ChannelType.PHONE)
                          || (c.activityType == ChannelActivityType.SMS && c.indskr_consentType == ChannelType.SMS)
                          || c.activityType == ChannelActivityType.WHATSAPP) {
                            c.values.push(
                              new ChannelValue(null, String(obj['value']), !!c.isChecked)
                            );
                        }
                      });
                    }
                  });
              }
              else if (type == ChannelActivityType.VISIT) {
                  this.contactService.contactInformation.addressesList.push(address);
                  this.allGeneratedTerms.value.forEach(term => {
                    if (term.channels && term.channels.length > 0) {
                      term.channels.forEach(c => {
                        if (c.activityType == ChannelActivityType.VISIT) {
                          c.values.push(
                            new ChannelValue(null, String(address.compositeAdd), !!c.isChecked)
                          );
                        }
                      });
                    }
                  });
              }
              this.contactService.contactInformation = this.contactService.getContactByID(contact.ID);
              loader.dismiss();
              resolve(true);
            } catch(error) {
              loader.dismiss();
              console.log("Error updating Contact", error);
              resolve(false);
            }
          } 
          // Offline mode
          else {
            // Channel values for pending push on Dynamics
            if (payload.contactid) {
              // Contact payload for pending push on Dynamics
              payload['pendingPushOnDynamics'] = true;

              const _id = DB_KEY_PREFIXES.OFFLINE_CONTACT_CHANNEL_VALUES + payload.contactid;
              const foundContact = await this.disk.retrieve(_id);
              if (foundContact) {
                // Check duplicated data
                const emailMap = new Map<string, Email>();
                foundContact['emails']?.forEach(email => {
                  emailMap.set(email.indskr_emailaddress, email);
                });
                payload['emails']?.forEach(email => {
                  emailMap.set(email.indskr_emailaddress, email);
                })
                payload['emails'] = Array.from(emailMap.values());
              }

              await this.disk.updateOrInsert(_id.toString(), doc => payload)
              .catch(error => console.error('saveOrUpdateConsentsChannelValuesInDB: ', error));

              //Update current contact info and consent term channel values
              //If consent is not captured with added values, added values should be removed from the offline updated data.
              if (type == ChannelActivityType.EMAIL) {
                let targetEmails = payload['emails'];
                if (targetEmails) {
                  const tempEmailAddressList = [];
                  for (let email of targetEmails) {
                    const offlineId = "offlineId" + '-' + new Date().getTime();
                    const emailObj: Email = {
                      emailAddress: email.indskr_emailaddress,
                      emailAddressId: offlineId,
                      isPrimary: email.indskr_isprimary,
                      isVerified: true
                    };
                    // Check duplicated data
                    const tempIdx = tempEmailAddressList.findIndex(t => t.emailAddress == emailObj.emailAddress);
                    if (tempIdx < 0) {
                      tempEmailAddressList.push(emailObj);
                    }
                    const contactIdx = this.contactService.contactInformation.emailAddressList.findIndex(c => c.emailAddress == emailObj.emailAddress);
                    if (contactIdx < 0) {
                      this.contactService.contactInformation.emailAddressList.push(emailObj);
                    }
                  }

                  this.allGeneratedTerms.value.forEach(term => {
                    if (term.channels && term.channels.length > 0) {
                      term.channels.forEach(c => {
                        if (c.activityType === ChannelActivityType.EMAIL || c.indskr_consentType === ChannelType.FACEBOOK) {
                          const emailAddressId = tempEmailAddressList.find(t => t.emailAddress == obj['indskr_emailaddress'])?.emailAddressId || "offlineId";
                          c.values.push(
                            new ChannelValue(emailAddressId, String(obj['indskr_emailaddress']), !!c.isChecked)
                          );
                        }
                      });
                    }
                  });
                }
              }
              else if (type == ChannelActivityType.PHONE || type == ChannelActivityType.SMS || type == ChannelActivityType.WHATSAPP) {
                this.contactService.contactInformation.mobilePhone = obj['value'];
                if (existingMobileNumber) {
                  this.contactService.contactInformation[obj['field']] = existingMobileNumber;
                }

                this.allGeneratedTerms.value.forEach(term => {
                  if (term.channels && term.channels.length > 0) {
                    term.channels.forEach(c => {
                      if ((c.activityType == ChannelActivityType.PHONE && c.indskr_consentType == ChannelType.PHONE)
                        || (c.activityType == ChannelActivityType.SMS && c.indskr_consentType == ChannelType.SMS)
                        || c.activityType == ChannelActivityType.WHATSAPP) {
                          c.values.push(
                            new ChannelValue(null, String(obj['value']), !!c.isChecked)
                          );
                      }
                    });
                  }
                });
              }
              //In case of ChannelActivityType.VISIT
              //Adding offline address is not supported because addres lookup is online. So, 'add' button is disabled in offline mode.
              this.contactService.contactInformation = this.contactService.getContactByID(this.contactService.contactInformation.ID);

              await this.loadOfflineAddContactMetadata();
            }
            loader.dismiss();
            resolve(true);
          }
          
        })
    });
  }

  doesContactHaveConsentForChannel(
                                    contact: { ID: string, emailAddressList?: any[], mobilePhone?: string, indskr_facebookpsid?: string },
                                    channelType: ChannelType, scheduledStart: Date, productId?: string
                                  ): ConsentedContact {
    if (!contact || !ChannelType) {
      return;
    }

    let hasConsent = false;
    let consentedContact: ConsentedContact = new ConsentedContact();
    // const activeConsentsWithContactId = this.allActiveConsentSubject.getValue();

    let contactConsents: ContactConsent[] = [];
    this.allActiveConsentSubject.subscribe(cc => {
      contactConsents = cc;
    });
    const activeConsentsForTheContact = Array.isArray(contactConsents) ? contactConsents
                    .filter(a => a.indskr_contactid === contact.ID)
                    .flatMap(item => item.activeConsents)
                    .filter(cntTerm => {
                      const sod = parseFloat(cntTerm.indskr_fromdate.toString());
                      const eod = parseFloat(cntTerm.indskr_untildate.toString());

                      return !isNaN(sod) && !isNaN(eod) && this.dateTimeFormatsService.isFutureDate(this.dateTimeFormatsService.getEndOfDay(eod)) &&
                        this.dateTimeFormatsService.isDateWithinRange(scheduledStart, this.dateTimeFormatsService.getStartOfDay(sod), this.dateTimeFormatsService.getEndOfDay(eod));
                    })
                    .filter(consent => Array.isArray(consent.channels) && consent.channels.some(channel => channel.indskr_consentType === channelType))
                    : null;

    // Reset email selectable field if channel is Email
    if (channelType === ChannelType.EMAIL && Array.isArray(contact.emailAddressList)) {
      for (let i = 0; i < contact.emailAddressList.length; i++) {
        const emailItem = contact.emailAddressList[i];
        emailItem.isSelectable = false;
        emailItem.hasConsented = false;
      }
    }
    if (Array.isArray(activeConsentsForTheContact)) {
      for (let i = 0; i < activeConsentsForTheContact.length; i++) {
        const activeConsent = activeConsentsForTheContact[i];

        // To check whether Product Consent flag is true
        // then check if template is selected, filter out by product first else skip
        if (this.authService.user.isProductConsent) {
          if (productId !== undefined && Array.isArray(activeConsent.products)) {
            const hasProduct = activeConsent.products.some(p => p.indskr_productid === productId);
            if (!hasProduct) {
              continue;
            }
          }
        }
        if (Array.isArray(activeConsent.channels)) {
          const channel = activeConsent.channels.find(c => c.indskr_consentType === channelType);
          if (channel) {
            switch (channelType) {
              case ChannelType.WHATSAPP:
              case ChannelType.SMS:
                hasConsent = contact.mobilePhone && contact.mobilePhone.length > 0 && Array.isArray(channel.values) && channel.values.some(p => p.value === contact.mobilePhone);
                break;

              case ChannelType.FACEBOOK:
                hasConsent = (contact.indskr_facebookpsid || '').length > 0;
                break;

              case ChannelType.EMAIL:
                if (Array.isArray(channel.values) && Array.isArray(contact.emailAddressList)) {
                  for (let i = 0; i < contact.emailAddressList.length; i++) {
                    const emailItem = contact.emailAddressList[i];
                    if (channel.values.some(v => (v.value === emailItem.emailAddress))) {
                      emailItem.isSelectable = true;
                      emailItem.hasConsented = true;
                      hasConsent = true;
                    }
                  }
                }
                break;

              default:
                break;
            }
          }
        }
      }
    }
    consentedContact.hasConsented = hasConsent;
    consentedContact.contact = contact;

    return consentedContact;
  }

  consentValidationBeforeSend (
                                contact: { ID: string, emailAddressList?: any[], mobilePhone?: string, indskr_facebookpsid?: string },
                                channelType: ChannelType, scheduledStart: Date, productId: string
                              ): boolean {
    const now = new Date();
    const activeConsentsWithContactId = this.allActiveConsentSubject.getValue();
    const activeConsentsForTheContact = Array.isArray(activeConsentsWithContactId) ? activeConsentsWithContactId
                    .filter(a => a.indskr_contactid === contact.ID)
                    .flatMap(item => item.activeConsents)
                    .filter(cntTerm => {
                      let isValid = false;
                      const sod = parseFloat(cntTerm.indskr_fromdate.toString());
                      const eod = parseFloat(cntTerm.indskr_untildate.toString());

                      if (isNaN(sod) || isNaN(eod) || this.dateTimeFormatsService.isPastDate(eod)) {}
                      else if (channelType === ChannelType.EMAIL) {
                        isValid = this.dateTimeFormatsService.isFutureDate(scheduledStart) ? this.dateTimeFormatsService.isDateWithinRange(scheduledStart, this.dateTimeFormatsService.getStartOfDay(sod), this.dateTimeFormatsService.getEndOfDay(eod))
                                  : this.dateTimeFormatsService.isDateWithinRange(now, this.dateTimeFormatsService.getStartOfDay(sod), this.dateTimeFormatsService.getEndOfDay(eod))
                      } else {
                        isValid = this.dateTimeFormatsService.isDateWithinRange(now, this.dateTimeFormatsService.getStartOfDay(sod), this.dateTimeFormatsService.getEndOfDay(eod))
                      }

                      return isValid;
                    })
                    .filter(consent => Array.isArray(consent.channels) && consent.channels.some(channel => channel.indskr_consentType === channelType))
                    .filter(consent => !productId ? true : Array.isArray(consent.products) && consent.products.some(product => product.indskr_productid === productId))
                    : null;
    return Array.isArray(activeConsentsForTheContact) && activeConsentsForTheContact.length > 0;
  }

  displayChannelLabels(label: string) {
    let displayName: string = "";
    let translateddisplayName: string = "";
    switch (label) {
      case ChannelType.EMAIL.toString():
        displayName = ChannelType.EMAIL_ADDRESS.toString();
        break;
      case ChannelType.VISIT.toString():
        displayName = ChannelType.VISIT.toString();
        break;
      case ChannelType.PHONE.toString():
        displayName = ChannelType.PHONE_NUMBER.toString();
        break;
      default:
        displayName = label;
    }
    switch (displayName) {
      case 'Email':
        translateddisplayName = this.translate.instant('EMAIL');
        break;
      case 'Address':
        translateddisplayName = this.translate.instant('ADDRESS');
        break;
      case 'Phone':
        translateddisplayName = this.translate.instant('PHONE');
        break;
      case 'SMS':
        translateddisplayName = this.translate.instant('SMS');
        break;
      case 'Visit':
        translateddisplayName = this.translate.instant('VISIT');
        break;
      case 'WhatsApp':
        translateddisplayName = this.translate.instant('WHATSAPP');
        break;
      case 'Facebook':
        translateddisplayName = this.translate.instant('FACEBOOK');
        break;
      case 'Fax':
        translateddisplayName = this.translate.instant('FAX');
        break;
      case 'Allocation orders':
      case 'Allocation Orders':
        translateddisplayName = this.translate.instant('ALLOWCATION_ORDERS');
        break;
      default:
        translateddisplayName = displayName;
        break;
    }

    return translateddisplayName;
  }

  saveConsentTodisk(consent: ContactConsent){
    this.disk.updateDocWithIdAndRev(consent)
  }

  private async updateOfflineContactMetadata(offlineConsents: any): Promise<any> {
    try {
      if (Array.isArray(offlineConsents)) {
        // Process offline consents to update contact metadata
        let targetEmailValuesToPushOnDynamics = [];
        let targetPhoneValuesToPushOnDynamics = [];
        let offlineConsentsContactIds = [];
        
        offlineConsents.forEach(consent => {
          offlineConsentsContactIds.push(consent.indskr_contactid);
          if (consent.channels && consent.channels.length > 0) {
            consent.channels.forEach(ch => {
              if (ch.values && ch.values.length > 0) {
                if (ch.activityType == ChannelActivityType.EMAIL) {
                  ch.values.forEach(v => targetEmailValuesToPushOnDynamics.push(v.value));
                }
                if (ch.activityType == ChannelActivityType.PHONE) {
                  ch.values.forEach(v => targetPhoneValuesToPushOnDynamics.push(v.value));
                }
              }
            });
          }
        });

        await Promise.all(offlineConsentsContactIds.map(async contactId => {
          if (targetEmailValuesToPushOnDynamics.length > 0 || targetPhoneValuesToPushOnDynamics.length > 0) {
            const _id = DB_KEY_PREFIXES.OFFLINE_CONTACT_CHANNEL_VALUES + contactId;
            const foundContact = await this.disk.retrieve(_id);
            if (foundContact) {
              // email channel values
              if (targetEmailValuesToPushOnDynamics.length > 0) {
                const filteredEmails = foundContact['emails']?.filter(email =>
                  targetEmailValuesToPushOnDynamics.includes(email.indskr_emailaddress)
                ) || [];
                foundContact['emails'] = filteredEmails;
              }
              // phone channel values
              if (targetPhoneValuesToPushOnDynamics.length > 0) {
                const phoneFields = [
                  'mobilephone',
                  'indskr_alternatephone1',
                  'indskr_alternatephone2',
                  'indskr_alternatephone3'
                ];
                phoneFields.forEach(field => {
                  foundContact[field] = targetPhoneValuesToPushOnDynamics.includes(foundContact[field]) ? foundContact[field] : '';
                });
              }
              await this.disk.updateOrInsert(_id.toString(), () => foundContact)
                .catch(error => console.error('saveOrUpdateTargetValuesToPushOnDynamics: ', error));
            }
          }
        }));
      }
    } catch (error) {
      console.error('Error in updateOfflineContactMetadata:', error);
    }
  }


  public async loadOfflineAddContactMetadata(offlineConsents?: any): Promise<any> {
    try {  
      if (offlineConsents) {
        await this.updateOfflineContactMetadata(offlineConsents);
      }
      // Fetch offline updated contact metadata
      let offlineContactMeta: any[] = [];
      const data = await this.disk.batchFetch(DB_ALLDOCS_QUERY_OPTIONS.GET_ALL_OFFLINE_CONTACT_META_ADD);
      if (Array.isArray(data)) {
        await Promise.all(data.map(async offlineData => {
          if (offlineData.pendingPushOnDynamics) {
            offlineContactMeta.push(offlineData);
          } else {
            const _id = DB_KEY_PREFIXES.OFFLINE_CONTACT_CHANNEL_VALUES + offlineData.contactid;
            await this.disk.remove(_id.toString());
          }
        }));
      }
      // Track offline data count
      this.disk.setOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.CONTACT_META_ADD, offlineContactMeta.length);
      return offlineContactMeta;
    } catch (diskError) {
      console.log("Disk error caught", diskError);
    }
  }

  private extractEmailObjectsAndIds(res: any[]): { emailObjs: Email[], updatedContactIdList: string[] } {
    let updatedContactIdList: string[] = [];
    const emailObjs: Email[] = (Array.isArray(res) ? res : []).reduce<Email[]>((acc, list) => {
      if (Array.isArray(list)) {
          const emails = list.reduce<Email[]>((emailAcc, item) => {
              if (item && Array.isArray(item.emails)) {
                const itemEmails: Email[] = item.emails.map(email => ({
                  emailAddress: email.indskr_emailaddress ?? '',
                  emailAddressId: email.indskr_email_addressid ?? '',
                  isPrimary: email.indskr_isprimary ?? false,
                  isVerified: true,
                  statecode: 0,
                  contactId: item.contactid ?? ''
                }));
                emailAcc = emailAcc.concat(itemEmails);
              }
              if (item.contactid) {
                updatedContactIdList.push(item.contactid);
              }
              return emailAcc;
          }, []);
          acc = acc.concat(emails);
      }
      return acc;
    }, []);
    return { emailObjs, updatedContactIdList };
  }

  private async removeContactMetadataFromDisk(removeContactIdList: string[]): Promise<void> {
    try {
      await Promise.all(removeContactIdList.map(async contactId => {
        const _id = DB_KEY_PREFIXES.OFFLINE_CONTACT_CHANNEL_VALUES + contactId;
        try {
          await this.disk.remove(_id.toString());
        } catch (error) {
          console.error(`Failed to remove contact metadata for ID ${contactId}: `, error);
        }
      })
    );
    } catch (error) {
      console.error('Failed to remove some contact metadata: ', error);
    }
  }

  public async uploadAddContactMetadataAndMapping(offlineConsents: any): Promise<any> {
    try {
      // Load offline contact metadata - added email address, phone number
      let offlineContactMetaAdd = await this.loadOfflineAddContactMetadata(offlineConsents);
      if (isEmpty(offlineContactMetaAdd)) {
        return offlineConsents;
      }
      // Filter metadata that has matching consent
      let filteredMetadataAdd: any[] = [];
      let removeContactIdList: string[] = [];
      offlineContactMetaAdd.forEach(oData => {
        const hasConsent = offlineConsents.some(consent => consent.indskr_contactid === oData.contactid);
        if (hasConsent) {
          filteredMetadataAdd.push(oData);
        } else {
          removeContactIdList.push(oData.contactid);
        }
      });
      if (!isEmpty(removeContactIdList)) {
        await this.removeContactMetadataFromDisk(removeContactIdList);
      }
      
      // Create contact metadata
      const res = await this.contactDataService.createContacts(filteredMetadataAdd);
      if (!Array.isArray(res) || res.length === 0) {
        return offlineConsents;
      }
      // Extract email objects and updated contact IDs
      const { emailObjs, updatedContactIdList } = this.extractEmailObjectsAndIds(res);

      // Mapping added channel values and update email address id in the offline captured consents
      if (!isEmpty(emailObjs)) {
        emailObjs.forEach(emailObj => {
          // Update email information in allGeneratedTerms
          this.allGeneratedTerms.value.forEach(term => {
            term.channels?.forEach(channel => {
              if (channel.activityType === ChannelActivityType.EMAIL || channel.indskr_consentType === ChannelType.FACEBOOK) {
                const idx = channel.values?.findIndex(v => v.id === emailObj.emailAddressId || v.value === emailObj.emailAddress);
                if (idx > -1) {
                  channel.values[idx] = new ChannelValue(emailObj.emailAddressId, emailObj.emailAddress, !!channel.isChecked);
                } else {
                  channel.values.push(new ChannelValue(emailObj.emailAddressId, emailObj.emailAddress, !!channel.isChecked));
                }
              }
            });
          });
          // Update email information in offlineConsents
          offlineConsents.forEach(consent => {
            consent.channels?.forEach(ch => {
              if ((ch.activityType === ChannelActivityType.EMAIL || ch.indskr_consentType === ChannelType.FACEBOOK)) {
                ch.values?.forEach(v => {
                  if (v.id.includes("offlineId") && emailObj.emailAddress == v.value) {
                    v.id = emailObj.emailAddressId;
                  }
                });
              }
            });
          });
        });
      }
      
      // Remove processed contacts from disk and update DB
      if (!isEmpty(updatedContactIdList)) {
        filteredMetadataAdd.forEach(offlineData => {
          const isUpdated = updatedContactIdList.some(contactid => contactid == offlineData.contactid);
          if (isUpdated) {
            // To prevent duplicated data upload when a disk error occurs while deleting data.
            offlineData['pendingPushOnDynamics'] = false;
          }
        });
        await this.removeContactMetadataFromDisk(updatedContactIdList);
      }

      filteredMetadataAdd = filteredMetadataAdd.filter(offlineData => offlineData['pendingPushOnDynamics']);
      await this.disk.bulk(filteredMetadataAdd);
    
      // Track offline data count
      const offlineCount: number = filteredMetadataAdd.length;
      this.disk.setOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.CONTACT_META_ADD, offlineCount);

      return offlineConsents;

    } catch (error) {
      console.error('Error in uploadAddContactMetadataAndMapping:', error);
      return offlineConsents;
    }
  }

  private getChannelActivityType(consentTermId: any, consentTypeId: any): ChannelActivityType | undefined {
    if (!consentTermId || !consentTypeId) return;

    const allConsentTerms = this.allConsentTermsSubject.getValue();
    if (!allConsentTerms) return;

    return allConsentTerms.find(a=>a.indskr_consenttermsid == consentTermId)
      ?.channels.find(c=>c.indskr_consenttypeid == consentTypeId)?.activityType;
  }

  private checkEmailConsent(channel: any, emailAddressList: any[]): boolean {
    return Array.isArray(channel.values) && emailAddressList.some(emailItem => 
      channel.values.some(v => v.value === emailItem.emailAddress));
  }
   
  public hasActiveConsentsForEmail(
    contact: { ID: string, emailAddressList?: any[] }, 
    channelType: ChannelType, 
    scheduledStart: Date
  ): boolean {
      let hasConsent = false;
      if (!contact || !contact.emailAddressList) return hasConsent;
      
      const contactConsents = this.allActiveConsentSubject.getValue() || [];

      const activeConsentsForTheContact = Array.isArray(contactConsents) ? contactConsents
                    .filter(a => a.indskr_contactid === contact.ID)
                    .flatMap(item => item.activeConsents)
                    .filter(cntTerm => {
                      const sod = parseFloat(cntTerm.indskr_fromdate.toString());
                      const eod = parseFloat(cntTerm.indskr_untildate.toString());

                      return !isNaN(sod) && !isNaN(eod) && this.dateTimeFormatsService.isFutureDate(this.dateTimeFormatsService.getEndOfDay(eod)) &&
                        this.dateTimeFormatsService.isDateWithinRange(scheduledStart, this.dateTimeFormatsService.getStartOfDay(sod), this.dateTimeFormatsService.getEndOfDay(eod));
                    })
                    .filter(consent =>
                      Array.isArray(consent.channels) &&
                      consent.channels.some(channel =>
                        channel.indskr_consentType === channelType ||
                        ChannelActivityType.EMAIL === this.getChannelActivityType(consent.indskr_consenttermsid, channel.indskr_consenttypeid)
                      )
                    )
                    : null;

    if (Array.isArray(activeConsentsForTheContact) && !isEmpty(activeConsentsForTheContact)) {
      hasConsent = true;
    }
    
    return hasConsent;
  }

  /**
   * Calculates which emails should be deleted based on remaining active consents.
   * Only returns emails that have no remaining opt-in consents.
   * @param optOutEmailAddressList Set of email addresses being opted out
   * @param contactId Contact ID to check consents for
   * @returns Set of email addresses that should be deleted
   */
  private calculateEmailsToDelete(optOutEmailAddressList: Set<string>, contactId: string): Set<string> {
    if (!optOutEmailAddressList.size) return new Set<string>();
    const emailsWithActiveConsents = new Set<string>();
    const contactConsents = this.allActiveConsentSubject.getValue() || [];
    const activeConsentsForContact = contactConsents.find(
      consent => consent.indskr_contactid?.toString() === contactId.toString()
    );

    if (activeConsentsForContact?.activeConsents) {
      activeConsentsForContact.activeConsents
        .flatMap(term => term.consentActivity || [])
        .filter(activity => 
          activity.indskr_consentactivitytype === 100000000 && 
          activity.indskr_emailaddress
        )
        .forEach(activity => 
          emailsWithActiveConsents.add(activity.indskr_emailaddress.toString())
        );
    }
    
    // Only delete emails that have NO remaining active opt-in consents
    const emailsToDelete = new Set<string>();
    optOutEmailAddressList.forEach(emailAddressId => {
      if (!emailsWithActiveConsents.has(emailAddressId.toString())) {
        emailsToDelete.add(emailAddressId);
      }
    });

    return emailsToDelete;
  }

  private async updateEmailAddressForContact(tempConActivities: any[]) {
    if (isEmpty(tempConActivities)) return;

    const optOutEmailAddressList = new Set<string>();
    const contactId = tempConActivities[0]["indskr_Contact@odata.bind"] || '';

    tempConActivities.forEach(data => {
      const emailAddress = data["indskr_EmailAddress@odata.bind"] || '';
      if (emailAddress) {
        optOutEmailAddressList.add(emailAddress);
      }
    });

    if (!optOutEmailAddressList.size) return;

    // Calculate which emails should be deleted (only those with no remaining consents)
    const emailsToDelete = this.calculateEmailsToDelete(optOutEmailAddressList, contactId);

    if (!emailsToDelete.size) return;
    
    // Update contact - email address
    let foundContact = this.contactService.contacts.find(c => c.ID == contactId);
    if (foundContact) {
      if (!isEmpty(foundContact?.emailAddressList)) {
        foundContact.emailAddressList = foundContact.emailAddressList.filter(email => !emailsToDelete.has(email.emailAddress));
        if (foundContact.emailAddressList.length == 1) {
          foundContact.emailAddressList[0].isPrimary = true;
        }
      }
      this.contactService.contactInformation = this.contactService.getContactByID(contactId);
    }
    // Update linked entity - email address
    const emailAddressFromLE: any = await this.disk.retrieve("indskr_email_address");
    if (emailAddressFromLE?.raw && Array.isArray(emailAddressFromLE.raw)) {
      const dbEmailAddressLE = emailAddressFromLE.raw;
      const updatedEmailAddressLE = dbEmailAddressLE.filter(d => 
        d.contactid != contactId || 
        (d.contactid == contactId && !emailsToDelete.has(d["indskr_email_address.indskr_emailaddress"]))
      );
      await this.disk.updateOrInsert("indskr_email_address", doc => {
        doc = {
          raw: updatedEmailAddressLE,
        };
        return doc;
      })
      .catch(error => console.error('Save Forms to DB error: ', error));
    }
  }

  async loadConsentTermsFromDB() {
    try {
      await this.disk
      .batchFetch(DB_ALLDOCS_QUERY_OPTIONS.GET_ALL_CONSENT_TERMS)
      .then((data: ConsentTerm[]) => {
        this.allConsentTermsSubject.next(data);
        console.log(`loadConsentTermsFromDB(): active consent terms from disk : ${data ? data.length : 0}`);
      });
    } catch (error) {
      console.log(`loadConsentTermsFromDB(): failed to load active consent terms from disk`);
    }
    
  }

  async reloadActiveConsentsFromDB() {
    try {
      await this.disk
      .batchFetch(DB_ALLDOCS_QUERY_OPTIONS.GET_ALL_ACTIVE_CONSENTS)
      .then((data: ContactConsent[]) => {
        this.allActiveConsentSubject.next(data);
        console.log(`reloadActiveConsentsFromDB(): active consents from disk : ${data ? data.length : 0}`);
      });
    } catch (error) {
      console.log(`reloadActiveConsentsFromDB(): failed to load active consents from disk`);
    }
  }

  public async getContactEmailAddressByContactId(contactId: string): Promise<any[]> {
    let emailAddressList: any[] = [];
    let fetchXML = fetchQueries.fetchActiveEmailAddressListByContactId;
    fetchXML = fetchXML.replace('{contactId}', contactId);

    try {
      const emailAddressData = await this.dynamics.executeFetchQuery('indskr_email_addresses', fetchXML);
      if (Array.isArray(emailAddressData)) {
        emailAddressList = emailAddressData;
      }
    } catch (error) {
      console.log("Error fetching contact email address by contactId: ", error);
    }

    return emailAddressList;
  }

  public async updateConsentActivityEmailByContactId(contactId: string) {
    if (this.deviceService.isOffline) return;
    //Contact email addresses updated via the fetchContactsForConfiguredDisplay function take some time because they update the Search index, etc.
    //Therefore, to update active consent, fetch and use the contact email address information.
    const emailAddressList = await this.getContactEmailAddressByContactId(contactId);
   
    if (emailAddressList) {
      const contactConsents = this.allActiveConsentSubject.getValue();
      const targetContactConsents = contactConsents.find(d => d.indskr_contactid === contactId);
      let isUpdated: boolean = false;

      targetContactConsents.activeConsents.forEach(consent => {
        consent.channels.forEach(channel => {
          channel.values.forEach(value => {
            // Match the value id with emailAddressId
            emailAddressList.forEach(email => {
              const emailAddressId = email.indskr_email_addressid;
              const emailAddress = email.indskr_emailaddress
              if (value.id === emailAddressId && value.value != emailAddress) {
                isUpdated = true;
                // Replace value with the correct email address
                const oldValue = value.value;
                value.value = emailAddress;
                // If value is updated, update the email in consentActivity
                consent.consentActivity.forEach(activity => {
                  if (activity.indskr_emailaddress === oldValue) {
                    activity.indskr_emailaddress = emailAddress;
                  }
                });
              }
            });
          });
        });
      });
      
      if (isUpdated) {
        const _id = DB_KEY_PREFIXES.CONSENTS + contactId;
        let contactConsentDB: ContactConsent = await this.disk.retrieve(_id);
        if (contactConsentDB && contactConsentDB._rev) {
          targetContactConsents._rev = contactConsentDB._rev;
        }
        await this.disk.updateDocWithIdAndRev(targetContactConsents);
      }
    }
  }
    
}

